import React, { useMemo } from "react"
import { Button, Tag, Space, Typography, Flex, Dropdown, Menu } from "antd"
import { CheckCircleFilled, MoreOutlined } from "@ant-design/icons"
import styles from "./index.module.css"
import DeleteConfirmation from "../modal/DeleteConfirmation"
import { useNavigate } from "react-router-dom"
import { routes } from "router"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useTemplateActions } from "./hooks/useTemplateActions"
import { getMenuItems } from "./menuConfig"

const { Title, Paragraph, Text } = Typography

export const CustomTemplateCard = ({ id, title, timeAgo, description, publishedTags = [], status = "published", onEdit }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const {
    isDeleteModalVisible,
    isDeleting,
    handleEditClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleMenuClick
  } = useTemplateActions(id, status, onEdit)

  const menuItems = useMemo(() => getMenuItems(status, styles), [status])
  const menu = useMemo(() => <Menu items={menuItems} onClick={handleMenuClick} className={styles.dropdownMenu} />, [menuItems, handleMenuClick])

  const templateData = useMemo(() => ({ id, title, description, publishedTags, status }), [id, title, description, publishedTags, status])

  return (
    <>
      <DeleteConfirmation
        visible={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        loading={isDeleting}
        title="Delete Template"
        content="Are you sure you want to delete this template? This action cannot be undone."
      />
      <Flex className={styles.customCard} bodyStyle={{ padding: 0 }} bordered={false}>
        {/* Image/left section */}
        <button
          type="button"
          className={styles.customImage}
          onClick={() => navigate(routes.proSettingsTemplateDetails({ id }))}
          aria-label={`View details for ${title}`}
        >
          <div className={`${styles.circle} ${styles.big}`} aria-hidden="true"></div>
          <div className={`${styles.circle} ${styles.small}`} aria-hidden="true"></div>
        </button>

        {/* Content/right section */}
        <div className={styles.cardDetails}>
          <div>
            <div className={styles.cardHeader}>
              <Title level={5} className={styles.cardTitle} onClick={() => navigate(routes.proSettingsTemplateDetails({ id }))}>
                {title}
              </Title>
            </div>
            <Paragraph style={{ fontSize: "14px" }} className={styles.cardDesc} ellipsis={{ rows: 2 }}>
              {description}
            </Paragraph>
          </div>
          <div className={styles.cardFooter}>
            {status === "Published" && (
              <Flex wrap gap={5} className={styles.cardTags}>
                <Text type="secondary" style={{ fontSize: "14px" }} className={styles.cardPublished}>
                  {status}
                </Text>
                {publishedTags.map((tag, idx) => (
                  <Tag icon={<CheckCircleFilled />} className={styles.publishedTag} key={tag + idx}>
                    {tag}
                  </Tag>
                ))}
              </Flex>
            )}
          </div>
          <Flex align="center" justify="space-between" className="mt-4">
            {status === "published" ? (
              <Space>
                <Button color="primary" variant="outlined" onClick={(e) => handleEditClick(e, templateData)}>
                  {t("settings.edit")}
                </Button>
              </Space>
            ) : (
              <Button color="primary" variant="outlined" onClick={(e) => handleEditClick(e, templateData)}>
                {t("settings.continue_creating")}
              </Button>
            )}
            <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight" overlayClassName={styles.dropdownOverlay}>
              <Button
                variant="text"
                color="primary"
                icon={<MoreOutlined />}
                className={styles.moreButton}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          </Flex>
        </div>
      </Flex>
    </>
  )
}

CustomTemplateCard.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string.isRequired,
  timeAgo: PropTypes.string,
  description: PropTypes.string,
  publishedTags: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      })
    ])
  ),
  status: PropTypes.string,
  onEdit: PropTypes.func
}
